import { Link } from '@tanstack/react-router';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';

export function DocsGettingStarted() {
  const t = useT();
  useDocumentTitle(t('docs.getting-started.title'));

  return (
    <DocsLayout
      path="/docs"
      title={t('docs.getting-started.label')}
      lede="Goalpost keeps a document’s notes, decisions, and reference material in one shared place, versioned as it changes. This guide walks you from signing up to your first approved change."
    >
      <h2 id="what-is-goalpost">What Goalpost does</h2>
      <p>
        Every effort has a document at its center: the plan for an event, the
        requirements for a product, the layout of a garden, the menu for a dinner.
        That document changes constantly, and the hard part is never writing it.
        It’s keeping everyone agreed on what it currently says.
      </p>
      <p>
        Goalpost gives that document structure and a memory. Its content is
        grouped into sections you can nest, from broad headings down to
        specifics. You change it in reviewable{' '}
        <strong>batches</strong>. And you decide who has to
        sign off before a change becomes official. The result is a shared
        document everyone can trust, with a full history of how it got there.
      </p>

      <h2 id="create-account">Create an account</h2>
      <p>
        Sign up with your email address. When you create an account, Goalpost sets
        up your <strong>workspace</strong>, your home for everything you do here. A
        workspace holds your documents, the people you work with, and your billing.
        You can be a member of more than one workspace (your own, plus a client’s or
        employer’s), and you switch between them from the bottom of the sidebar.
      </p>
      <p>
        New workspaces start on the free plan, which is enough to run real documents
        on your own. See{' '}
        <Link to="/docs/workspace" hash="account-types">
          Account types
        </Link>{' '}
        for what each plan includes.
      </p>

      <h2 id="first-document">Create your first document</h2>
      <p>
        A <strong>document</strong> is a structured list of lists that holds your
        notes, context, reference materials, requirements, and decisions. From
        your dashboard, choose{' '}
        <strong>New document</strong>, give it a name, and open it. A fresh document
        starts empty and ready for its first section.
      </p>
      <p>
        Full walkthrough:{' '}
        <Link to="/docs/documents" hash="create">
          Create a document
        </Link>
        .
      </p>

      <h2 id="add-details">Add content</h2>
      <p>
        Inside a document, add content to describe what you’re planning. Start
        broad, with a top-level section like “Living room” if you’re building a
        house, or “Checkout flow” if you’re building an e-commerce experience,
        then add items beneath it to get specific. There’s no need to plan the
        whole tree up front; it grows as your thinking does. An item can be a
        single sentence, a fully specified leaf like “Wall paint: Benjamin Moore
        OC-17,” a link, or an image.
      </p>
      <p>
        You can reorder, nest, and label everything you add. Every one of these
        changes is collected into a change batch you review before it takes effect,
        which is the next step.
      </p>

      <h2 id="submit-change">Submit a change</h2>
      <p>
        As you build out your document, you group all of the changes (additions,
        removals, modifications) together in a batch, which starts out as a{' '}
        <strong>draft</strong>. When the draft says what you want, you{' '}
        <strong>submit</strong> it. If the document requires approvals, the draft
        waits for them; if it doesn’t, the change takes effect immediately and
        becomes the next official version. Repeating this cycle over and over lets
        you iterate on your document, refining additional layers of detail and
        pruning scope, reflecting the iterative nature of planning that is central
        to how we work.
      </p>
      <p>
        This is the heart of Goalpost, and it’s covered in depth in{' '}
        <Link to="/docs/change-management">Change Management</Link>.
      </p>

      <h2 id="invite-team">Bring in your team</h2>
      <p>
        Working with other people, whether colleagues, a client, or a contractor?
        Create a <strong>team</strong>, invite them to it, and add that team to your
        document. Access to a document always flows through teams: anyone on a team
        you’ve added can see the document, and teams are how you decide who needs to
        approve changes.
      </p>
      <p>
        See <Link to="/docs/teams">Teams</Link> to set this up, and{' '}
        <Link to="/docs/approval-rules">Approval Rules</Link> to require
        sign-off before changes go through.
      </p>

      <h2 id="next-steps">Next steps</h2>
      <ul>
        <li>
          <Link to="/docs/workspace">Workspace</Link>: accounts, teams, invitations,
          and billing at a glance.
        </li>
        <li>
          <Link to="/docs/documents">Documents</Link>: creating, sharing, and managing
          access to a document.
        </li>
        <li>
          <Link to="/docs/change-management">Change Management</Link>: drafts,
          history, and the approval lifecycle.
        </li>
        <li>
          <Link to="/docs/approval-rules">Approval Rules</Link>: deciding
          who signs off, and how.
        </li>
        <li>
          <Link to="/docs/ai-integration">AI Integrations</Link>: let an AI assistant
          draft changes against your documents.
        </li>
      </ul>
    </DocsLayout>
  );
}
